// Quietroom — app.js
// Cards as thoughts, not controls

const MIN_ZOOM = 0.05;
const MAX_ZOOM = 3;
const DEFAULT_ZOOM = 1;
const DOTS_ZOOM_THRESHOLD = 0.2;

const APP_VERSION = "1.03";

function getAnonymousUserId() {
  try {
    const key = "quietroom_anon_user_id_v1";
    let id = localStorage.getItem(key);
    if (!id) {
      // простой случай: uuid
      const uuid = (crypto && crypto.randomUUID)
        ? crypto.randomUUID()
        : String(Date.now()) + "-" + Math.random().toString(16).slice(2);

      id = "u_" + uuid;
      localStorage.setItem(key, id);
    }
    return id;
  } catch {
    // если localStorage недоступен — делаем эпемерный id на запуск
    return "u_ephemeral_" + Date.now();
  }
}

// один sessionId на запуск (на вкладку)
const QUIETROOM_SESSION_ID = (function () {
  try {
    // если хочешь более «липкую» сессию, можно хранить её
    // в sessionStorage, но пока достаточно в памяти
    const uuid = (crypto && crypto.randomUUID)
      ? crypto.randomUUID()
      : String(Date.now()) + "-" + Math.random().toString(16).slice(2);

    return "s_" + uuid;
  } catch {
    return "s_" + Date.now();
  }
})();

function getAnalyticsEndpoint() {
  if (window.QuietroomAnalyticsEndpoint) {
    return window.QuietroomAnalyticsEndpoint;
  }

  const { protocol, hostname, origin } = window.location;

  // 1) Запуск с диска (file://)
  if (protocol === "file:") {
    return "https://localhost:7096/api/events";
  }

  // 2) Локальный сайт под IIS / Kestrel
  if (hostname === "localhost" || hostname === "127.0.0.1") {
    return "https://localhost:7096/api/events";
  }

  // 3) Прод
  if (hostname.endsWith("quietroom.app")) {
    return "https://quietroom.app/analytics/api/events";
  }

  // 4) Фолбек
  if (origin && origin !== "null") {
    return origin.replace(/\/$/, "") + "/analytics/api/events";
  }

  return "https://localhost:7096/api/events";
}

const ANALYTICS_ENDPOINT = getAnalyticsEndpoint();

function logEvent(name, payload) {
  try {
    const basePayload = {
      version: APP_VERSION,
      userId: getAnonymousUserId(),
      sessionId: QUIETROOM_SESSION_ID,
    };

    const body = {
      name,
      payload: payload
        ? { ...basePayload, ...payload }
        : basePayload,
      timestamp: new Date().toISOString(),
      referrer: document.referrer || null,
    };

    fetch(ANALYTICS_ENDPOINT, {
      method: "POST",
      keepalive: true,
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify(body),
    }).catch(() => {});
  } catch {
    // аналитика не должна ломать приложение
  }
}

const state = {
  camera: { x: 0, y: 0, zoom: 1 },
  cards: [],
  draggingCardId: null,
  dragOffset: { x: 0, y: 0 },
  focusedCardId: null, // используется только когда открыт редактор
  hasSeenIntro: false,
  homePosition: null, // координаты первой созданной карточки
  activeCardId: null, // карточка, с которой сейчас работаем (клик/стрелки)
  themeId: "warm_daylight", // тема по умолчанию
};

const THEMES = {
  warm_daylight: {
    id: "warm_daylight",
    name: "Warm Daylight",

    background: "#F7F3EC",

    card: {
      background: "#FFFFFF",
      border: "rgba(15, 23, 42, 0.06)",
      text: "#111827",

      shadow: "rgba(15, 23, 42, 0.12)",
      shadowActive: "rgba(230, 181, 124, 0.65)",

      borderActive: "rgba(230, 181, 124, 0.7)",
    },
  },

  night_ink: {
    id: "night_ink",
    name: "Night Ink",

    background: "#020617",

    card: {
      background: "#0B1220",

      // рамка стала слабее (меньше "стекла")
      border: "rgba(148, 163, 184, 0.22)",
      borderActive: "rgba(248, 250, 252, 0.55)",

      text: "#E5E7EB",

      // тень мягче и шире
      shadow: "rgba(15, 23, 42, 0.78)",
      shadowActive: "rgba(248, 250, 252, 0.20)",
    },

    hints: {
      color: "rgba(226, 232, 240, 0.85)",
      glow: "0 0 12px rgba(248, 250, 252, 0.25)",
    },
  },

  // задел на будущее ↓
  /*
  cool_morning: { ... },
  warm_evening: { ... },
  */
};

let currentTheme = THEMES.warm_daylight;

const STORAGE_KEY = "quietroom_state_v1";

let canvas, ctx;
let isPanning = false;
let panStart = { x: 0, y: 0 };
let cameraStart = { x: 0, y: 0 };

let editor = { el: null, card: null };
let hintsEl = null;
let homeButtonEl = null;
let themeToggleButtonEl = null;

// анимация камеры
let cameraAnimation = null; // { fromX, fromY, toX, toY, startTime, duration }

const isTouchDevice =
  "ontouchstart" in window ||
  (navigator && navigator.maxTouchPoints > 0);

// ---- card layout constants ----
const CARD_WIDTH = 240;
const CARD_MAX_HEIGHT = 360;
const CARD_PADDING = 12;
const CARD_LINE_HEIGHT = 20;
const CARD_FONT_SIZE = 14;
const CARD_FONT =
  `${CARD_FONT_SIZE}px system-ui, -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif`;

// ------------------------------------------------
// persistence
// ------------------------------------------------

function markIntroSeen() {
  if (!state.hasSeenIntro) {
    state.hasSeenIntro = true;
    saveState();
  }
}

function loadState() {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return;
    const saved = JSON.parse(raw);
    if (saved.camera) state.camera = saved.camera;
    if (Array.isArray(saved.cards)) {
      state.cards = saved.cards;
      state.cards.forEach(ensureCardVisuals);
    }
    if (typeof saved.hasSeenIntro === "boolean")
      state.hasSeenIntro = saved.hasSeenIntro;

    if (
      saved.homePosition &&
      typeof saved.homePosition.x === "number" &&
      typeof saved.homePosition.y === "number"
    ) {
      state.homePosition = saved.homePosition;
    } else if (!state.homePosition && state.cards.length > 0) {
      const first = state.cards[0];
      state.homePosition = { x: first.x, y: first.y };
    }

    if (!state.camera.zoom || typeof state.camera.zoom !== "number") {
      state.camera.zoom = 1;
    }
    if (state.camera.zoom < MIN_ZOOM) state.camera.zoom = MIN_ZOOM;
    if (state.camera.zoom > MAX_ZOOM) state.camera.zoom = MAX_ZOOM;

    // тема из сохранённого состояния
    if (typeof saved.themeId === "string" && THEMES[saved.themeId]) {
      state.themeId = saved.themeId;
      currentTheme = THEMES[saved.themeId];
    }
  } catch {}
}

function saveState() {
  localStorage.setItem(
    STORAGE_KEY,
    JSON.stringify({
      camera: state.camera,
      cards: state.cards,
      hasSeenIntro: state.hasSeenIntro,
      homePosition: state.homePosition,
      themeId: state.themeId || currentTheme.id,
    })
  );
}

// ------------------------------------------------
// themes
// ------------------------------------------------

function applyTheme() {
  if (!currentTheme) return;

  // --- фон приложения ---
  if (currentTheme.id === "night_ink") {
    // ночной фон с очень мягким "дыханием"
    document.body.style.background =
      "radial-gradient(1200px at 50% 40%, #050b1c 0%, #020617 60%)";
  } else {
    // дневная тема — плоский фон
    document.body.style.background = currentTheme.background;
  }

  // --- подсказки ---
  if (hintsEl) {
    if (currentTheme.hints) {
      hintsEl.style.color = currentTheme.hints.color;
      hintsEl.style.textShadow = currentTheme.hints.glow || "none";
    } else {
      hintsEl.style.color = "rgba(15, 23, 42, 0.7)";
      hintsEl.style.textShadow = "none";
    }
  }

  // можно слегка адаптировать кнопку темы под текущую тему
  updateThemeToggleButton();
}

function setTheme(id, source) {
  const t = THEMES[id];
  if (!t) return;
  currentTheme = t;
  state.themeId = id;
  applyTheme();

  // событие аналитики
  logEvent("theme_changed", {
    theme: id,
    source, // например "keyboard" или "button"
  });

  saveState();

  updateThemeToggleButton();

  if (typeof logEvent === "function") {
    logEvent("theme_changed", {
      theme: id,
      source: source || "unknown",
    });
  }
}

// ------------------------------------------------
// helpers
// ------------------------------------------------

function worldToScreen(x, y) {
  return {
    x: (x - state.camera.x) * state.camera.zoom,
    y: (y - state.camera.y) * state.camera.zoom,
  };
}

function screenToWorld(x, y) {
  return {
    x: x / state.camera.zoom + state.camera.x,
    y: y / state.camera.zoom + state.camera.y,
  };
}

function updateHintsVisibility() {
  if (!hintsEl) return;
  hintsEl.style.display = "block";
  const baseOpacity = state.focusedCardId ? 0.35 : 0.85;
  hintsEl.style.opacity =
    currentTheme.id === "night_ink" ? Math.min(1, baseOpacity + 0.15) : baseOpacity;
}

// bounding box всех карточек с небольшим запасом
function getCardsBounds() {
  if (!state.cards.length) return null;

  let minX = Infinity;
  let maxX = -Infinity;
  let minY = Infinity;
  let maxY = -Infinity;

  for (const c of state.cards) {
    if (c.x < minX) minX = c.x;
    if (c.y < minY) minY = c.y;
    if (c.x + c.width > maxX) maxX = c.x + c.width;
    if (c.y + c.height > maxY) maxY = c.y + c.height;
  }

  const margin = 40; // мир чуть вокруг мыслей

  return {
    left: minX - margin,
    right: maxX + margin,
    top: minY - margin,
    bottom: maxY + margin,
  };
}

// Абсолютные границы мира: достаточно большой, но конечный.
// На минимальном зуме весь мир ровно помещается в экран.
function getWorldBounds() {
  if (!canvas) return null;

  const worldWidth = canvas.width / MIN_ZOOM;
  const worldHeight = canvas.height / MIN_ZOOM;

  const halfW = worldWidth / 2;
  const halfH = worldHeight / 2;

  return {
    left: -halfW,
    right: halfW,
    top: -halfH,
    bottom: halfH,
  };
}

// Ограничиваем камеру так, чтобы текущий экран всегда оставался
// внутри мира и пересекался с областью карточек (если они есть)
function clampCameraWithinBounds() {
  if (!canvas) return;

  const world = getWorldBounds();
  const bounds = getCardsBounds();

  const vw = canvas.width / state.camera.zoom;  // ширина экрана в мировых координатах
  const vh = canvas.height / state.camera.zoom;

  // 1) базовые ограничения от мира: экран целиком внутри мира
  let minCamX = world ? world.left : -Infinity;
  let maxCamX = world ? world.right - vw : Infinity;
  let minCamY = world ? world.top : -Infinity;
  let maxCamY = world ? world.bottom - vh : Infinity;

  // 2) дополнительные мягкие ограничения от bounds карточек
  if (bounds) {
    // по горизонтали хотим, чтобы была видна хотя бы "большая половина" карточки
    const basePeekX = CARD_WIDTH * 0.6; // ~60% ширины карточки
    // по вертикали оставляем как раньше
    const basePeekY = 80;

    const width = bounds.right - bounds.left;
    const height = bounds.bottom - bounds.top;

    // для маленьких областей не требуем слишком большого "хвоста"
    const peekX = Math.min(basePeekX, width / 2);
    const peekY = Math.min(basePeekY, height / 2);

    const cardsMinCamX = bounds.left + peekX - vw;
    const cardsMaxCamX = bounds.right - peekX;

    const cardsMinCamY = bounds.top + peekY - vh;
    const cardsMaxCamY = bounds.bottom - peekY;

    if (isFinite(minCamX)) minCamX = Math.max(minCamX, cardsMinCamX);
    else minCamX = cardsMinCamX;

    if (isFinite(maxCamX)) maxCamX = Math.min(maxCamX, cardsMaxCamX);
    else maxCamX = cardsMaxCamX;

    if (isFinite(minCamY)) minCamY = Math.max(minCamY, cardsMinCamY);
    else minCamY = cardsMinCamY;

    if (isFinite(maxCamY)) maxCamY = Math.min(maxCamY, cardsMaxCamY);
    else maxCamY = cardsMaxCamY;
  }

  let camX = state.camera.x;
  let camY = state.camera.y;

  // если пересечение выродилось — центрируем камеру по миру
  if (minCamX > maxCamX || minCamY > maxCamY) {
    if (world) {
      camX = world.left + (world.right - world.left - vw) / 2;
      camY = world.top + (world.bottom - world.top - vh) / 2;
    }
  } else {
    if (camX < minCamX) camX = minCamX;
    if (camX > maxCamX) camX = maxCamX;
    if (camY < minCamY) camY = minCamY;
    if (camY > maxCamY) camY = maxCamY;
  }

  state.camera.x = camX;
  state.camera.y = camY;
}

// вернуть координаты камеры, "притянутые" к допустимым
function getClampedCameraPosition(x, y) {
  const prevX = state.camera.x;
  const prevY = state.camera.y;

  state.camera.x = x;
  state.camera.y = y;
  clampCameraWithinBounds();

  const clamped = { x: state.camera.x, y: state.camera.y };

  state.camera.x = prevX;
  state.camera.y = prevY;

  return clamped;
}

// запускаем анимацию камеры к цели
function animateCameraTo(targetX, targetY, duration = 260) {
  if (!canvas) return;
  
  const clamped = getClampedCameraPosition(targetX, targetY);

  cameraAnimation = {
    fromX: state.camera.x,
    fromY: state.camera.y,
    toX: clamped.x,
    toY: clamped.y,
    startTime: performance.now(),
    duration,
  };
}

// центр карточки в мировых координатах
function getCardCenter(card) {
  return {
    x: card.x + card.width / 2,
    y: card.y + card.height / 2,
  };
}

// мягко центрируем камеру на карточке
function centerCameraOnCard(card) {
  if (!canvas || !card) return;

  const zoom = state.camera.zoom || 1;
  const center = getCardCenter(card);

  const targetX = center.x - canvas.width / (2 * zoom);
  const targetY = center.y - canvas.height / (2 * zoom);

  animateCameraTo(targetX, targetY);
}

// выбираем "текущую" карточку для навигации,
// если activeCardId нет — берём ближайшую к центру экрана
function getOrSelectActiveCard() {
  if (state.activeCardId) {
    const existing = state.cards.find(c => c.id === state.activeCardId);
    if (existing) return existing;
    state.activeCardId = null;
  }

  if (!canvas || !state.cards.length) return null;

  const viewCenter = {
    x: state.camera.x + canvas.width / (2 * state.camera.zoom),
    y: state.camera.y + canvas.height / (2 * state.camera.zoom),
  };

  let best = null;
  let bestDist = Infinity;

  for (const c of state.cards) {
    const cc = getCardCenter(c);
    const dx = cc.x - viewCenter.x;
    const dy = cc.y - viewCenter.y;
    const dist2 = dx * dx + dy * dy;
    if (dist2 < bestDist) {
      bestDist = dist2;
      best = c;
    }
  }

  if (best) {
    state.activeCardId = best.id;
  }

  return best;
}

// находим карточку в заданном направлении относительно current
function findNextCardInDirection(current, direction) {
  if (!current || !state.cards.length) return null;

  const curCenter = getCardCenter(current);
  const epsilon = 8; // чтобы избежать выбора почти на линии

  let best = null;
  let bestScore = Infinity;

  for (const c of state.cards) {
    if (c.id === current.id) continue;
    const cc = getCardCenter(c);
    const dx = cc.x - curCenter.x;
    const dy = cc.y - curCenter.y;

    let inDir = false;
    let primary = 0;
    let secondary = 0;

    switch (direction) {
      case "left":
        if (dx < -epsilon) {
          inDir = true;
          primary = Math.abs(dx);
          secondary = Math.abs(dy);
        }
        break;
      case "right":
        if (dx > epsilon) {
          inDir = true;
          primary = Math.abs(dx);
          secondary = Math.abs(dy);
        }
        break;
      case "up":
        if (dy < -epsilon) {
          inDir = true;
          primary = Math.abs(dy);
          secondary = Math.abs(dx);
        }
        break;
      case "down":
        if (dy > epsilon) {
          inDir = true;
          primary = Math.abs(dy);
          secondary = Math.abs(dx);
        }
        break;
    }

    if (!inDir) continue;

    // ближе по направлению + чуть штраф за "косость"
    const score = primary * 10 + secondary;
    if (score < bestScore) {
      bestScore = score;
      best = c;
    }
  }

  return best;
}

// Камера домой: к первой карточке (с анимацией)
function goHome() {
  if (!canvas || !state.homePosition) return;

  // отменяем любую текущую анимацию камеры
  cameraAnimation = null;

  // ставим дефолтный зум
  let zoom = DEFAULT_ZOOM;
  if (zoom < MIN_ZOOM) zoom = MIN_ZOOM;
  if (zoom > MAX_ZOOM) zoom = MAX_ZOOM;
  state.camera.zoom = zoom;

  const viewWidth = canvas.width / zoom;
  const viewHeight = canvas.height / zoom;

  // теоретический центр мира так, чтобы homePosition была в центре экрана
  let targetX = state.homePosition.x - viewWidth / 2;
  let targetY = state.homePosition.y - viewHeight / 2;

  // просто напрямую ставим камеру
  state.camera.x = targetX;
  state.camera.y = targetY;

  // и один раз подрезаем по ограничениям мира + карточек
  clampCameraWithinBounds();

  saveState();

  // аналитика: прыжок домой
  logEvent("go_home", {
    hasHome: true,
    cardsCount: state.cards.length,
  });
}

// Кнопка Home (H) в UI справа (desktop + mobile)
function createHomeButton() {
  if (homeButtonEl) return homeButtonEl;

  const btn = document.createElement("button");
  homeButtonEl = btn;

  btn.textContent = "H";
  btn.title = "Go home (first thought)";

  Object.assign(btn.style, {
    position: "fixed",
    top: "16px",
    right: "16px",
    left: "auto",
    width: "32px",
    height: "32px",
    borderRadius: "999px",
    border: "none",
    padding: "0",
    fontSize: "14px",
    cursor: "pointer",
    opacity: "0.55",
    backdropFilter: "blur(8px)",
    background: "rgba(250, 249, 246, 0.95)",
    boxShadow: "0 4px 12px rgba(15,23,42,0.18)",
    transition: "opacity 0.15s ease, transform 0.15s ease",
    zIndex: "1000",
    display: "none", // по умолчанию скрыта, покажем отдельно
  });

  btn.addEventListener("mouseenter", () => {
    btn.style.opacity = "0.95";
    btn.style.transform = "translateY(-1px)";
  });

  btn.addEventListener("mouseleave", () => {
    btn.style.opacity = "0.55";
    btn.style.transform = "translateY(0)";
  });

  btn.addEventListener("click", () => {
    goHome();
  });

  document.body.appendChild(btn);
  return btn;
}

function updateHomeButtonVisibility() {
  if (!homeButtonEl) return;
  homeButtonEl.style.display = state.cards.length > 1 ? "block" : "none";
}

// Кнопка переключения темы (UI)
function createThemeToggleButton() {
  if (themeToggleButtonEl) return themeToggleButtonEl;

  const btn = document.createElement("button");
  themeToggleButtonEl = btn;

  btn.type = "button";
  btn.title = "Toggle theme";

  Object.assign(btn.style, {
    position: "fixed",
    top: "16px",
    right: "56px", // левее кнопки H
    width: "32px",
    height: "32px",
    borderRadius: "999px",
    border: "none",
    padding: "0",
    fontSize: "14px",
    cursor: "pointer",
    opacity: "0.65",
    backdropFilter: "blur(8px)",
    background: "rgba(250, 249, 246, 0.95)",
    boxShadow: "0 4px 12px rgba(15,23,42,0.18)",
    transition: "opacity 0.15s ease, transform 0.15s ease",
    zIndex: "1000",
  });

  btn.addEventListener("mouseenter", () => {
    btn.style.opacity = "0.95";
    btn.style.transform = "translateY(-1px)";
  });

  btn.addEventListener("mouseleave", () => {
    btn.style.opacity = "0.65";
    btn.style.transform = "translateY(0)";
  });

  btn.addEventListener("click", () => {
    const nextId =
      currentTheme.id === "warm_daylight" ? "night_ink" : "warm_daylight";
    setTheme(nextId, "button");
  });

  document.body.appendChild(btn);
  updateThemeToggleButton();

  return btn;
}

function updateThemeToggleButton() {
  if (!themeToggleButtonEl) return;

  // показываем, во что переключится по клику
  if (currentTheme.id === "night_ink") {
    // сейчас ночь → кнопка предлагает день
    themeToggleButtonEl.textContent = "☀";
  } else {
    // сейчас день → кнопка предлагает ночь
    themeToggleButtonEl.textContent = "☾";
  }

  // лёгкая адаптация фона под тему
  if (currentTheme.id === "night_ink") {
    themeToggleButtonEl.style.background = "rgba(15, 23, 42, 0.9)";
    themeToggleButtonEl.style.color = "rgba(248, 250, 252, 0.95)";
  } else {
    themeToggleButtonEl.style.background = "rgba(250, 249, 246, 0.95)";
    themeToggleButtonEl.style.color = "#111827";
  }
}

// Hotkeys (desktop): H = home, стрелки = переход между карточками, T = theme
function onKeyDown(e) {
  const active = document.activeElement;
  if (active === editor.el) return;

  // переключение темы: T
  if (e.code === "KeyT") {
    e.preventDefault();
    const nextId =
      currentTheme.id === "warm_daylight" ? "night_ink" : "warm_daylight";
    setTheme(nextId, "keyboard");
    return;
  }

  // H — по физической клавише, независимо от раскладки
  if (e.code === "KeyH") {
    e.preventDefault();
    goHome();
    return;
  }

  // стрелки — навигация между карточками
  if (
    e.code === "ArrowLeft" ||
    e.code === "ArrowRight" ||
    e.code === "ArrowUp" ||
    e.code === "ArrowDown"
  ) {
    e.preventDefault();

    const current = getOrSelectActiveCard();
    if (!current) return;

    let direction = null;
    if (e.code === "ArrowLeft") direction = "left";
    else if (e.code === "ArrowRight") direction = "right";
    else if (e.code === "ArrowUp") direction = "up";
    else if (e.code === "ArrowDown") direction = "down";

    const next = findNextCardInDirection(current, direction);
    if (!next) return;

    state.activeCardId = next.id;
    centerCameraOnCard(next);
  }
}

// ------------------------------------------------
// cards (разбиение текста уже было починено — не трогаем)
// ------------------------------------------------

function measureCard(text) {
  ctx.save();
  ctx.font = CARD_FONT;

  const maxW = CARD_WIDTH - CARD_PADDING * 2;
  const maxLines = Math.floor(
    (CARD_MAX_HEIGHT - CARD_PADDING * 2) / CARD_LINE_HEIGHT
  );

  const lines = [];
  let truncated = false;

  const raw = (text || "").replace(/\r\n/g, "\n");
  const paragraphs = raw.split("\n");

  outer: for (const para of paragraphs) {
    const words = para.split(/\s+/);
    let line = "";

    for (const word of words) {
      if (!word) continue;

      const test = line ? line + " " + word : word;
      const testWidth = ctx.measureText(test).width;
      const wordWidth = ctx.measureText(word).width;

      if (testWidth <= maxW) {
        // слово влезает в строку целиком — просто добавляем
        line = test;
        continue;
      }

      // текущая строка заполнена — переносим её в lines
      if (line) {
        if (lines.length < maxLines) {
          lines.push(line);
        } else {
          truncated = true;
          break outer;
        }
        line = "";
      }

      if (wordWidth <= maxW) {
        // слово целиком помещается в пустую строку
        line = word;
      } else {
        // слово само по себе длиннее строки — режем по символам
        let part = "";
        for (const ch of word) {
          const testPart = part + ch;
          if (ctx.measureText(testPart).width <= maxW) {
            part = testPart;
          } else {
            if (lines.length < maxLines) {
              lines.push(part);
            } else {
              truncated = true;
              break outer;
            }
            part = ch;
          }
        }
        line = part;
      }
    }

    if (truncated) break outer;

    if (line) {
      if (lines.length < maxLines) {
        lines.push(line);
      } else {
        truncated = true;
        break outer;
      }
    }
  }

  if (!lines.length) {
    lines.push("");
  }

  // если текста больше, чем влезло в lines — добавляем многоточие
  if (truncated && lines.length) {
    let last = lines[lines.length - 1];
    const ellipsis = "…";

    // укорачиваем последнюю строку, пока "…"
    // не будет помещаться по ширине
    while (
      last.length > 0 &&
      ctx.measureText(last + ellipsis).width > maxW
    ) {
      last = last.slice(0, -1);
    }
    lines[lines.length - 1] = last + ellipsis;
  }

  const height =
    CARD_PADDING * 2 + lines.length * CARD_LINE_HEIGHT;

  ctx.restore();

  return {
    width: CARD_WIDTH,
    height: Math.min(height, CARD_MAX_HEIGHT),
    lines,
    visibleLines: lines.length,
  };
}

// --- visual identity (variant D) ---

function ensureStarProps(card) {
  if (
    card.starPhase == null ||
    card.starPulse == null ||
    card.starSize == null
  ) {
    card.starPhase = Math.random() * Math.PI * 2;
    card.starPulse = 0.6 + Math.random() * 0.6;
    card.starSize = 0.8 + Math.random() * 0.6;
  }
}

// --- visual identity (variant D) ---
function ensureCardVisuals(card) {
  // базовая геометрия карточки
  if (typeof card.baseRotation !== "number") {
    card.baseRotation = ((Math.random() * 8) - 4) * Math.PI / 180; // ±4°
  }
  if (typeof card.baseScale !== "number") {
    card.baseScale = 0.96 + Math.random() * 0.08;                 // 0.96–1.04
  }

  if (typeof card.currentRotation !== "number") {
    card.currentRotation = card.baseRotation;
  }
  if (typeof card.currentScale !== "number") {
    card.currentScale = card.baseScale;
  }

  // "звёздные" параметры для режима точек (один раз на карту)
  if (!Number.isFinite(card.starPhase)) {
    card.starPhase = Math.random() * Math.PI * 2;           // случайная фаза
  }
  if (!Number.isFinite(card.starSpeed)) {
    card.starSpeed = 0.3 + Math.random() * 0.5;             // 0.3–0.8, скорость мерцания
  }
  if (!Number.isFinite(card.starTwinkle)) {
    card.starTwinkle = 0.12 + Math.random() * 0.08;         // 0.12–0.2, глубина мерцания
  }
  if (!Number.isFinite(card.starSize)) {
    card.starSize = 0.8 + Math.random() * 0.6;              // 0.8–1.4, разброс размеров
  }

  // параметры случайного мерцания (звёзды)
  if (!Number.isFinite(card.twinkleUntil)) {
    card.twinkleUntil = 0;
  }
  if (!Number.isFinite(card.twinkleStrength)) {
    card.twinkleStrength = 0;
  }
  if (!Number.isFinite(card.nextTwinkleAt)) {
    // первая вспышка через 1–6 секунд
    card.nextTwinkleAt = performance.now() + 1000 + Math.random() * 5000;
  }
}

function createCard(x, y) {
  const card = {
    id: Date.now().toString(),
    x,
    y,
    text: "",
    isNew: true,
  };

  const layout = measureCard(" ");
  card.width = layout.width;
  card.height = layout.height;

  ensureCardVisuals(card);

  state.cards.push(card);

  if (!state.homePosition) {
    state.homePosition = { x, y };
  }

  updateHomeButtonVisibility();
  saveState();

  // аналитика: новая мысль
  logEvent("card_created", {
    cardsCount: state.cards.length,
  });

  return card;
}

function findCardAt(x, y) {
  for (let i = state.cards.length - 1; i >= 0; i--) {
    const c = state.cards[i];
    if (
      x >= c.x &&
      x <= c.x + c.width &&
      y >= c.y &&
      y <= c.y + c.height
    ) {
      return c;
    }
  }
  return null;
}

// ------------------------------------------------
// editor
// ------------------------------------------------

function openEditor(card) {
  editor.card = card;
  editor.originalText = card.text;
  editor.originalHeight = card.height;

  state.focusedCardId = card.id;
  updateHintsVisibility();

  const p = worldToScreen(card.x, card.y);

  editor.el.value = card.text;

  editor.el.style.left = `${p.x}px`;
  editor.el.style.top = `${p.y}px`;
  const zoomForEditor = Math.max(state.camera.zoom, 1);
  editor.el.style.width = `${card.width * zoomForEditor}px`;
  editor.el.style.display = "block";

  editor.el.style.height = "auto";
  const maxPx = CARD_MAX_HEIGHT * state.camera.zoom;
  const initialH = Math.min(editor.el.scrollHeight, maxPx);
  editor.el.style.height = initialH + "px";
  card.height = initialH / state.camera.zoom;

  editor.el.focus();
}

function closeEditor(commit) {
  const card = editor.card;
  if (!card) return;

  const newText = editor.el.value;
  const trimmed = newText.trim();

  if (!commit) {
    // ESC — отмена
    if (card.isNew) {
      // новая пустая — убираем
      state.cards = state.cards.filter((c) => c.id !== card.id);
      if (state.cards.length === 0) {
        state.homePosition = null;
      }
      if (state.activeCardId === card.id) {
        state.activeCardId = null;
      }
    } else {
      // откат к исходному тексту/высоте
      if (typeof editor.originalText === "string") {
        card.text = editor.originalText;
      }
      if (typeof editor.originalHeight === "number") {
        card.height = editor.originalHeight;
      } else {
        const layout = measureCard(card.text);
        card.height = layout.height;
      }
    }
  } else {
    // blur — сохранить
    if (trimmed === "") {
      // ЛЮБАЯ пустая карточка (новая или старая) удаляется
      state.cards = state.cards.filter((c) => c.id !== card.id);
      if (state.cards.length === 0) {
        state.homePosition = null;
      }
      if (state.activeCardId === card.id) {
        state.activeCardId = null;
      }
    } else {
      card.text = newText;
      card.isNew = false;

      const layout = measureCard(card.text);
      card.width = layout.width;
      card.height = layout.height;
      markIntroSeen();
    }
  }

  editor.card = null;
  editor.el.style.display = "none";
  state.focusedCardId = null;
  updateHintsVisibility();

  editor.originalText = undefined;
  editor.originalHeight = undefined;

  updateHomeButtonVisibility();
  saveState();
}

function onEditorInput() {
  const el = editor.el;
  if (!el) return;

  el.style.height = "auto";

  const maxPx = CARD_MAX_HEIGHT * state.camera.zoom;
  const newH = Math.min(el.scrollHeight, maxPx);

  el.style.height = newH + "px";

  if (editor.card) {
    editor.card.height = newH / state.camera.zoom;
  }
}

// ------------------------------------------------
// rendering
// ------------------------------------------------

function roundedRect(ctx, x, y, w, h, r) {
  r = Math.min(r, w / 2, h / 2);
  ctx.beginPath();
  ctx.moveTo(x + r, y);
  ctx.lineTo(x + w - r, y);
  ctx.quadraticCurveTo(x + w, y, x + w, y + r);
  ctx.lineTo(x + w, y + h - r);
  ctx.quadraticCurveTo(x + w, y + h, x + w - r, y + h);
  ctx.lineTo(x + r, y + h);
  ctx.quadraticCurveTo(x, y + h, x, y + h - r);
  ctx.lineTo(x, y + r);
  ctx.quadraticCurveTo(x, y, x + r, y);
  ctx.closePath();
}

function drawCardDot(card, isActive) {
  const center = getCardCenter(card);

  let zoom = state.camera.zoom;
  if (!Number.isFinite(zoom) || zoom <= 0) zoom = 1;

  // --- размер звезды ---
  const invZoom = 1 / Math.max(zoom, 0.05);
  let sizeScale = 1 + (invZoom - 1) * 0.8;
  sizeScale = Math.min(3.0, Math.max(1.0, sizeScale));

  const baseCore = isActive ? 3.0 : 2.4;
  const baseHalo = isActive ? 7.5 : 6.0;

  const coreR = baseCore * sizeScale;
  const haloR = baseHalo * sizeScale;

  if (
    !Number.isFinite(center.x) ||
    !Number.isFinite(center.y) ||
    !Number.isFinite(coreR) ||
    !Number.isFinite(haloR) ||
    haloR <= 0
  ) {
    return;
  }

  const now = performance.now();

  // --- запуск редких вспышек ---
  if (now > card.nextTwinkleAt && card.twinkleUntil < now) {
    if (Math.random() < 0.7) { // чуть чаще
      const duration = 160 + Math.random() * 360; // 160–520 мс
      card.twinkleUntil = now + duration;

      // базовая сила вспышки (потом усилится зумом/темой)
      card.twinkleStrength = 0.35 + Math.random() * 0.45; // 0.35–0.8
    }
    // следующая возможная вспышка через 1.5–6.5 секунд
    card.nextTwinkleAt = now + 1500 + Math.random() * 5000;
  }

  // --- базовая яркость ---
  let alpha = 1;

  // --- фазa активной вспышки ---
  if (card.twinkleUntil > now) {
    // прогресс вспышки 0..1
    const total = 400; // нормируем по 400 мс
    const t = 1 - (card.twinkleUntil - now) / total;
    const clampedT = Math.max(0, Math.min(1, t));

    // форма вспышки: быстро вверх, мягко вниз
    const envelope = Math.sin(clampedT * Math.PI);

    // усиливаем вспышку на маленьком зуме и на светлой теме
    const zoomBoost = 0.8 + (sizeScale - 1) * 1.0;       // ~0.8–2.8
    const themeBoost = currentTheme.id === "night_ink" ? 1.0 : 1.5;

    const extra =
      envelope *
      (card.twinkleStrength || 0.5) *
      zoomBoost *
      themeBoost;

    alpha += extra;
  }

  // клампим, чтобы не было совсем неадекватно
  alpha = Math.max(0.7, Math.min(2.2, alpha));

  // --- цвета ---
  let coreColor;
  let haloColor;

  if (currentTheme.id === "night_ink") {
    const coreA = 0.90 * Math.min(alpha, 1.6);
    const haloA = 0.12 * alpha;
    coreColor = `rgba(248, 250, 252, ${coreA.toFixed(3)})`;
    haloColor = `rgba(248, 250, 252, ${haloA.toFixed(3)})`;
  } else {
    const coreA = 0.90 * Math.min(alpha, 1.7);
    const haloA = 0.10 * alpha; // светлой теме чуть сильнее ореол
    coreColor = `rgba(15, 23, 42, ${coreA.toFixed(3)})`;
    haloColor = `rgba(15, 23, 42, ${haloA.toFixed(3)})`;
  }

  ctx.save();

  const g = ctx.createRadialGradient(
    center.x, center.y, 0,
    center.x, center.y, haloR
  );

  g.addColorStop(0, coreColor);
  g.addColorStop(coreR / haloR, coreColor);
  g.addColorStop(1, haloColor);

  ctx.fillStyle = g;
  ctx.beginPath();
  ctx.arc(center.x, center.y, haloR, 0, Math.PI * 2);
  ctx.fill();

  // небольшое ядро
  ctx.fillStyle = coreColor;
  ctx.beginPath();
  ctx.arc(center.x, center.y, coreR * 0.7, 0, Math.PI * 2);
  ctx.fill();

  ctx.restore();
}

// dimmed — когда открыт редактор для другой карточки
// active — кликнутая/выбранная карточка (подсветка тени)
function drawCard(card, dimmed, active) {
  ctx.save();

  const cx = card.x + card.width / 2;
  const cy = card.y + card.height / 2;

  ctx.translate(cx, cy);
  ctx.rotate(card.currentRotation || 0);
  ctx.scale(card.currentScale || 1, card.currentScale || 1);
  ctx.translate(-card.width / 2, -card.height / 2);

  if (dimmed) ctx.globalAlpha = 0.18;

  ctx.save();
  const baseBlur = 28 / state.camera.zoom;
  const baseOffsetY = 10 / state.camera.zoom;

  const theme = currentTheme.card;

  // --- тень ---
  if (active) {
    ctx.shadowColor = theme.shadowActive;
    ctx.shadowBlur = baseBlur * 1.4;
    ctx.shadowOffsetY = baseOffsetY;
  } else {
    ctx.shadowColor = theme.shadow;
    ctx.shadowBlur = baseBlur;
    ctx.shadowOffsetY = baseOffsetY;
  }

  // --- тело карточки ---
  roundedRect(ctx, 0, 0, card.width, card.height, 12);
  ctx.fillStyle = theme.background;
  ctx.fill();
  ctx.restore();

  // --- обводка ---
  ctx.lineWidth = 1 / state.camera.zoom;
  ctx.strokeStyle = active
    ? theme.borderActive
    : theme.border;

  roundedRect(ctx, 0, 0, card.width, card.height, 12);
  ctx.stroke();

  // --- текст ---
  ctx.fillStyle = theme.text;
  ctx.font = CARD_FONT;
  ctx.textBaseline = "top";

  const layout = measureCard(card.text);
  layout.lines.slice(0, layout.visibleLines).forEach((line, i) => {
    ctx.fillText(
      line,
      CARD_PADDING,
      CARD_PADDING + i * CARD_LINE_HEIGHT
    );
  });

  ctx.restore();
}

function drawIntro() {
  // показываем призрачную карточку только,
  // если нет мыслей и онбординг ещё не пройден
  if (state.cards.length > 0 || state.hasSeenIntro) return;
  if (!canvas) return;

  ctx.save();
  ctx.setTransform(1, 0, 0, 1, 0, 0); // экранные координаты

  const ghostWidth = CARD_WIDTH;
  const ghostHeight = 120;
  const centerX = canvas.width / 2;
  const centerY = canvas.height / 2;

  // лёгкий наклон, чтобы карточка выглядела "живой"
  const angle = -3 * Math.PI / 180;

  // палитра для дня/ночи
  let bg = "#ffffff";
  let textColor = "rgba(39,48,58,0.6)";
  let shadowColor = "rgba(15,23,42,0.12)";
  let alpha = 0.35;
  let shadowBlur = 50;

  if (currentTheme && currentTheme.id === "night_ink") {
    bg = "#0B1220";
    textColor = "rgba(226,232,240,0.7)";
    shadowColor = "rgba(15,23,42,0.85)";
    alpha = 0.45;
    shadowBlur = 70;
  }

  // --- рисуем саму карточку с наклоном ---
  ctx.translate(centerX, centerY);
  ctx.rotate(angle);

  ctx.shadowColor = shadowColor;
  ctx.shadowBlur = shadowBlur;
  ctx.shadowOffsetX = 0;
  ctx.shadowOffsetY = 0;

  ctx.globalAlpha = alpha;
  roundedRect(ctx, -ghostWidth / 2, -ghostHeight / 2, ghostWidth, ghostHeight, 12);
  ctx.fillStyle = bg;
  ctx.fill();

  // рамку не рисуем — пусть будет совсем мягкий силуэт
  ctx.shadowColor = "transparent";
  ctx.globalAlpha = 1;

  // --- текст внутри карточки ---

  const ghostText = isTouchDevice
    ? "Double-tap to write a thought…"
    : "Double-click to write a thought…";

  ctx.font = 'italic 14px system-ui, -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif';
  ctx.fillStyle = textColor;
  ctx.textAlign = "center";
  ctx.textBaseline = "middle";

  ctx.fillText(ghostText, 0, 0);

  ctx.restore();
}

function draw(timestamp) {
  // анимация камеры
  if (cameraAnimation) {
    const now = performance.now();
    const { fromX, fromY, toX, toY, startTime, duration } = cameraAnimation;
    const tRaw = (now - startTime) / duration;
    const t = Math.max(0, Math.min(1, tRaw));
    // smoothstep / ease-in-out
    const k = t * t * (3 - 2 * t);

    state.camera.x = fromX + (toX - fromX) * k;
    state.camera.y = fromY + (toY - fromY) * k;

    if (t >= 1) {
      cameraAnimation = null;
      clampCameraWithinBounds();
      saveState();
    }
  }

  ctx.setTransform(1, 0, 0, 1, 0, 0);
  ctx.clearRect(0, 0, canvas.width, canvas.height);

  ctx.save();
  ctx.translate(
    -state.camera.x * state.camera.zoom,
    -state.camera.y * state.camera.zoom
  );
  ctx.scale(state.camera.zoom, state.camera.zoom);

  const lerp = 0.18;

  const showDots = state.camera.zoom < DOTS_ZOOM_THRESHOLD;

  state.cards.forEach((c) => {
    ensureCardVisuals(c);

    if (editor.card && c.id === editor.card.id) return;

    if (showDots) {
      const isActive = state.activeCardId === c.id;
      drawCardDot(c, isActive);
      return;
    }

    const focused = state.focusedCardId === c.id;
    const tr = focused ? 0 : c.baseRotation;
    const ts = focused ? 1 : c.baseScale;

    c.currentRotation += (tr - c.currentRotation) * 0.18;
    c.currentScale += (ts - c.currentScale) * 0.18;

    const dimmed = !!state.focusedCardId && !focused;
    const isActive = state.activeCardId === c.id || focused;

    drawCard(c, dimmed, isActive);
  });

  ctx.restore();
  drawIntro();
  requestAnimationFrame(draw);
}

// ------------------------------------------------
// mouse (desktop)
// ------------------------------------------------

function onMouseDown(e) {
  cameraAnimation = null; // ручное управление отменяет анимацию

  const rect = canvas.getBoundingClientRect();
  const wx = e.clientX - rect.left;
  const wy = e.clientY - rect.top;
  const w = screenToWorld(wx, wy);

  const card = findCardAt(w.x, w.y);

  if (card) {
    state.draggingCardId = card.id;
    state.dragOffset = { x: w.x - card.x, y: w.y - card.y };
    state.activeCardId = card.id;
  } else {
    isPanning = true;
    panStart = { x: e.clientX, y: e.clientY };
    cameraStart = { ...state.camera };
    state.activeCardId = null;
  }
}

function onMouseMove(e) {
  if (isPanning) {
    state.camera.x =
      cameraStart.x - (e.clientX - panStart.x) / state.camera.zoom;
    state.camera.y =
      cameraStart.y - (e.clientY - panStart.y) / state.camera.zoom;
    clampCameraWithinBounds();
  }

  if (state.draggingCardId) {
    const rect = canvas.getBoundingClientRect();
    const wx = e.clientX - rect.left;
    const wy = e.clientY - rect.top;
    const w = screenToWorld(wx, wy);

    const c = state.cards.find((c) => c.id === state.draggingCardId);
    if (c) {
      c.x = w.x - state.dragOffset.x;
      c.y = w.y - state.dragOffset.y;

      const world = getWorldBounds();
      if (world) {
        if (c.x < world.left) c.x = world.left;
        if (c.x + c.width > world.right) c.x = world.right - c.width;
        if (c.y < world.top) c.y = world.top;
        if (c.y + c.height > world.bottom) c.y = world.bottom - c.height;
      }
    }
  }
}

function onMouseUp() {
  if (isPanning || state.draggingCardId) saveState();
  isPanning = false;
  state.draggingCardId = null;
}

function onDblClick(e) {
  cameraAnimation = null; // переход в редактор — без анимации камеры

  const rect = canvas.getBoundingClientRect();
  const wx = e.clientX - rect.left;
  const wy = e.clientY - rect.top;
  const w = screenToWorld(wx, wy);

  const card = findCardAt(w.x, w.y);
  if (card) state.activeCardId = card.id;
  openEditor(card || createCard(w.x, w.y));
}

// ------------------------------------------------
// wheel zoom (desktop)
// ------------------------------------------------

function onWheel(e) {
  if (editor.card) {
    e.preventDefault();
    return;
  }

  cameraAnimation = null; // ручное масштабирование отменяет анимацию
  e.preventDefault();

  const zoomFactor = 1 - e.deltaY * 0.001;
  const newZoom = Math.min(
    MAX_ZOOM,
    Math.max(MIN_ZOOM, state.camera.zoom * zoomFactor)
  );

  const rect = canvas.getBoundingClientRect();
  const mx = e.clientX - rect.left;
  const my = e.clientY - rect.top;

  const before = screenToWorld(mx, my);
  state.camera.zoom = newZoom;
  const after = screenToWorld(mx, my);

  state.camera.x += before.x - after.x;
  state.camera.y += before.y - after.y;

  clampCameraWithinBounds();
  saveState();
}

// ------------------------------------------------
// touch: pan, pinch zoom, drag card
// ------------------------------------------------

let lastTouchDist = null;
let touchPanStart = null;
let touchCameraStart = null;
let touchDraggingCardId = null;
let touchDragOffset = { x: 0, y: 0 };

function onTouchStart(e) {
  if (editor.card) return;

  cameraAnimation = null; // жесты отменяют анимацию

  if (e.touches.length === 1) {
    const t = e.touches[0];
    const rect = canvas.getBoundingClientRect();
    const wx = t.clientX - rect.left;
    const wy = t.clientY - rect.top;
    const w = screenToWorld(wx, wy);

    const card = findCardAt(w.x, w.y);

    if (card) {
      touchDraggingCardId = card.id;
      touchDragOffset = { x: w.x - card.x, y: w.y - card.y };
      touchPanStart = null;
      touchCameraStart = null;
      state.activeCardId = card.id;
    } else {
      touchDraggingCardId = null;
      touchPanStart = { x: t.clientX, y: t.clientY };
      touchCameraStart = { ...state.camera };
      state.activeCardId = null;
    }

    lastTouchDist = null;
  } else if (e.touches.length === 2) {
    const t0 = e.touches[0];
    const t1 = e.touches[1];
    const dx = t0.clientX - t1.clientX;
    const dy = t0.clientY - t1.clientY;
    lastTouchDist = Math.hypot(dx, dy);

    touchDraggingCardId = null;
    touchPanStart = null;
    touchCameraStart = null;
  }
}

function onTouchMove(e) {
  if (editor.card) return;

  // ─── ОДИН ПАЛЕЦ ──────────────────────────────────────────
  if (e.touches.length === 1) {
    const t = e.touches[0];

    // Перетаскивание карточки
    if (touchDraggingCardId) {
      e.preventDefault();

      const rect = canvas.getBoundingClientRect();
      const wx = t.clientX - rect.left;
      const wy = t.clientY - rect.top;
      const w = screenToWorld(wx, wy);

      const card = state.cards.find((c) => c.id === touchDraggingCardId);
      if (card) {
        card.x = w.x - touchDragOffset.x;
        card.y = w.y - touchDragOffset.y;

        const world = getWorldBounds();
        if (world) {
          if (card.x < world.left) card.x = world.left;
          if (card.x + card.width > world.right) card.x = world.right - card.width;
          if (card.y < world.top) card.y = world.top;
          if (card.y + card.height > world.bottom) card.y = world.bottom - card.height;
        }
      }

      return;
    }

    // Панорамирование
    if (touchPanStart && touchCameraStart) {
      e.preventDefault();

      const dx = t.clientX - touchPanStart.x;
      const dy = t.clientY - touchPanStart.y;

      state.camera.x = touchCameraStart.x - dx / state.camera.zoom;
      state.camera.y = touchCameraStart.y - dy / state.camera.zoom;

      clampCameraWithinBounds();
      saveState();
      return;
    }

    return;
  }

  // ─── ДВА ПАЛЬЦА: PINCH-ZOOM ──────────────────────────────
  if (e.touches.length === 2) {
    e.preventDefault();

    const t0 = e.touches[0];
    const t1 = e.touches[1];

    const dx = t0.clientX - t1.clientX;
    const dy = t0.clientY - t1.clientY;
    const dist = Math.hypot(dx, dy);

    // центр между пальцами в экранных координатах
    const centerClientX = (t0.clientX + t1.clientX) / 2;
    const centerClientY = (t0.clientY + t1.clientY) / 2;

    const rect = canvas.getBoundingClientRect();
    const mx = centerClientX - rect.left;
    const my = centerClientY - rect.top;

    if (lastTouchDist) {
      const zoomFactor = dist / lastTouchDist;
      const newZoom = Math.min(
        MAX_ZOOM,
        Math.max(MIN_ZOOM, state.camera.zoom * zoomFactor)
      );

      // мир до изменения зума под центром жеста
      const before = screenToWorld(mx, my);

      state.camera.zoom = newZoom;

      // мир после изменения зума под тем же экранным центром
      const after = screenToWorld(mx, my);

      // сдвигаем камеру так, чтобы точка под пальцами осталась на месте
      state.camera.x += before.x - after.x;
      state.camera.y += before.y - after.y;

      clampCameraWithinBounds();
      saveState();
    }

    lastTouchDist = dist;
  }
}

function onTouchEnd(e) {
  if (e.touches.length === 0) {
    lastTouchDist = null;
    touchPanStart = null;
    touchCameraStart = null;
    touchDraggingCardId = null;
  } else if (e.touches.length === 1) {
    const t = e.touches[0];
    touchDraggingCardId = null;
    lastTouchDist = null;
    touchPanStart = { x: t.clientX, y: t.clientY };
    touchCameraStart = { ...state.camera };
  }
}

// ------------------------------------------------
// init
// ------------------------------------------------

function init() {
  canvas = document.getElementById("canvas");
  ctx = canvas.getContext("2d");
  editor.el = document.getElementById("card-editor");
  hintsEl = document.querySelector(".app-hints");

  canvas.width = window.innerWidth;
  canvas.height = window.innerHeight;

  loadState();

  // если по какой-то причине themeId не валидный — остаёмся на warm_daylight
  if (state.themeId && THEMES[state.themeId]) {
    currentTheme = THEMES[state.themeId];
  } else {
    state.themeId = currentTheme.id;
  }

  applyTheme();
  clampCameraWithinBounds();

  logEvent("app_open", {
    theme: currentTheme.id,
    cardsCount: state.cards.length,
    isTouch: isTouchDevice,
  });

  if (hintsEl) {
    hintsEl.textContent = isTouchDevice
      ? "Tap & drag — move · Double-tap — thought · Pinch — zoom"
      : "Double-click — thought · Drag — move · Scroll — zoom · Arrows — navigate · H — home · T — theme";
  }

  updateHintsVisibility();
  createHomeButton();
  updateHomeButtonVisibility();
  createThemeToggleButton();

  window.addEventListener("resize", () => {
    canvas.width = window.innerWidth;
    canvas.height = window.innerHeight;
    clampCameraWithinBounds();
  });

  canvas.addEventListener("mousedown", onMouseDown);
  window.addEventListener("mousemove", onMouseMove);
  window.addEventListener("mouseup", onMouseUp);
  canvas.addEventListener("dblclick", onDblClick);
  canvas.addEventListener("wheel", onWheel, { passive: false });

  if (isTouchDevice) {
    canvas.addEventListener("touchstart", onTouchStart, { passive: true });
    canvas.addEventListener("touchmove", onTouchMove, { passive: false });
    canvas.addEventListener("touchend", onTouchEnd);
    canvas.addEventListener("touchcancel", onTouchEnd);
  }

  editor.el.addEventListener("blur", () => closeEditor(true));
  editor.el.addEventListener("keydown", (e) => {
    if (e.key === "Escape") closeEditor(false);
  });

  editor.el.addEventListener("input", onEditorInput);

  window.addEventListener("keydown", onKeyDown);

  requestAnimationFrame(draw);
}

document.addEventListener("DOMContentLoaded", init);
